import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';
import { Button, Destructive, Icon, Popover } from '@efira/ui';

import { useLayout } from '../../api/queries';
import type { HudElementSpec, LayoutPayload } from '../../api/queries';
import {
  FRAME_HEIGHT,
  FRAME_WIDTH,
  boxOf,
  guardZones,
  SNAP,
  watermarkBox,
} from '../../layout/geometry';
import { useLayoutEditor } from '../../layout/useLayoutEditor';
import type { LayoutEditor } from '../../layout/useLayoutEditor';

/**
 * Режим раскладки ПОВЕРХ ЖИВОГО ОВЕРЛЕЯ.
 *
 * Оверлей в кадре продолжает идти; над ним появляются подвижные части -
 * рамка, подпись над рамкой, угловая ручка - и полупрозрачные охранные зоны
 * TikTok. Заголовков, описаний и схемы кадра здесь нет: кадр и есть схема
 * (реестр, `console.center.hud-btn`; снимок эталона `preview-layout-editor`).
 *
 * До 2026-09-19 кнопка показывала в кадре прежний экран «Раскладка overlay»
 * целиком - получался кадр внутри кадра. Механика при переезде не
 * переписана: снап, направляющие, история, охранные зоны и перенос между
 * играми живут в `useLayoutEditor` и общие с прежним экраном.
 *
 * Координаты - логические пиксели кадра 1080x1920; на экран они переводятся
 * процентами, поэтому слой не зависит от того, какого размера кадр сейчас.
 */
const STEP = 4;
const FAST_STEP = 40;

export function FrameLayout({ gameId, onExit }: { gameId: string; onExit: () => void }) {
  const query = useLayout(gameId);

  if (query.data === undefined) {
    return (
      <p className="text-foreground absolute inset-0 grid place-items-center text-sm">
        {query.isError ? 'Раскладка не загрузилась' : 'Раскладка загружается…'}
      </p>
    );
  }

  return <Editable gameId={gameId} data={query.data} onExit={onExit} />;
}

/**
 * Перемонтирование редактора при замене раскладки сервером.
 *
 * Ключом по самой раскладке делать нельзя: каждое сохранение возвращает её же,
 * редактор перемонтируется и теряет историю отмены - кнопка «Отменить»
 * остаётся неактивной после первого шага.
 */
function Editable({
  gameId,
  data,
  onExit,
}: {
  gameId: string;
  data: LayoutPayload;
  onExit: () => void;
}) {
  const [epoch, setEpoch] = useState(0);

  const bump = useCallback(() => {
    setEpoch((value) => value + 1);
  }, []);

  return (
    <Surface
      /* Пустой состав и пришедший - разные редакторы: местная раскладка
         редактора берётся из данных один раз, при монтировании, и состав,
         пришедший позже (`useLayout`, опрос пустого состава), без
         перемонтирования остался бы без своих мест. */
      key={`${gameId}:${epoch}:${data.elements.length === 0 ? 'empty' : 'ready'}`}
      gameId={gameId}
      data={data}
      onExit={onExit}
      onReload={bump}
    />
  );
}

function Surface({
  gameId,
  data,
  onExit,
  onReload,
}: {
  gameId: string;
  data: LayoutPayload;
  onExit: () => void;
  onReload: () => void;
}) {
  const editor = useLayoutEditor(gameId, data, onReload);
  const host = useRef<HTMLDivElement>(null);
  const scale = useFrameScale(host);

  return (
    <>
      <div
        ref={host}
        data-console-layout-editor
        className="absolute inset-0"
        onPointerDown={() => {
          editor.select(null);
        }}
      >
        <GuardZones />

        {data.elements.map((element) => (
          <Movable key={element.id} element={element} editor={editor} scale={scale} />
        ))}

        <Guides editor={editor} />

        <WatermarkChip scale={scale} />

        {data.elements.length === 0 && (
          <p className="text-foreground absolute inset-x-4 top-1/3 rounded bg-black/70 p-3 text-center text-sm">
            Состав частей сообщает сам оверлей, когда рисует сцену. Если оверлей пуст, подождите,
            пока игра нарисует первую сцену.
          </p>
        )}
      </div>

      {/* Управление уезжает в шапку оверлея: внутри оверлея любая панель
          закрывает то, ради чего режим включён. */}
      <ControlsSlot>
        <Controls data={data} editor={editor} onExit={onExit} />
      </ControlsSlot>
    </>
  );
}

/**
 * ВЫСОТА ПОДПИСИ НАД РАМКОЙ - В ПИКСЕЛЯХ ЭКРАНА, А КООРДИНАТЫ ЧАСТЕЙ - В
 * ЛОГИЧЕСКИХ. Чтобы сравнить одно с другим, нужен масштаб кадра: сколько
 * пикселей экрана приходится на логическую единицу. Кадр тянется вместе с
 * колонкой, поэтому он меряется наблюдателем, а не однажды при отрисовке.
 */
function useFrameScale(host: React.RefObject<HTMLDivElement | null>): number {
  const [scale, setScale] = useState(0);

  useEffect(() => {
    const node = host.current;

    if (node === null) return;

    const watch = new ResizeObserver(() => {
      setScale(node.getBoundingClientRect().height / FRAME_HEIGHT);
    });

    watch.observe(node);

    return () => {
      watch.disconnect();
    };
  }, [host]);

  return scale;
}

/**
 * Хватает ли места НАД рамкой на подпись.
 *
 * Решение владельца 2026-09-25: не хватает - подпись уходит внутрь, под
 * верхнюю кромку самой части, тем же приёмом, которым подписи охранных зон
 * раскрываются внутрь кадра. Обрезанная кромкой подпись не называет часть,
 * а место занимает.
 *
 * Масштаб 0 - кадр ещё не измерен: считаем, что место есть, иначе все
 * подписи прыгнут внутрь на первом кадре и вернутся на втором.
 */
const CAPTION_PX = 18;

function captionFits(topLogical: number, scale: number): boolean {
  return scale === 0 || topLogical * scale >= CAPTION_PX;
}

/** Портал в панель инструментов кадра. Её рисует центр колонки - см. `CenterColumn`. */
function ControlsSlot({ children }: { children: React.ReactNode }) {
  const host = document.querySelector('[data-frame-toolbar-slot]');

  return host === null ? null : createPortal(children, host);
}

/**
 * Водяной знак в режиме раскладки: НАЖИМАЕТСЯ, НО НЕ ДВИГАЕТСЯ.
 *
 * Знак - условие бесплатного доступа, и делать вид, что его можно отнести
 * в угол или спрятать, нельзя: подвижная рамка вокруг него обещала бы ровно
 * это. Поэтому у него нет ни рамки, ни ручки - есть нажатие, ведущее туда,
 * где написано, почему он здесь и что даёт Pro.
 *
 * Положение берётся из `watermarkBox()` - того же места, из которого его
 * берёт сам оверлей: две копии координат разъехались бы в первый же сдвиг
 * охранной зоны.
 */
function WatermarkChip({ scale }: { scale: number }) {
  const navigate = useNavigate();
  const box = watermarkBox();
  const above = captionFits(box.top, scale);

  return (
    <Button
      variant="secondary"
      /* Подпись ВЫНЕСЕНА НАД РАМКОЙ, а не лежит в ней: внутри она
         закрывала сам водяной знак, ради которого рамку и рисуют
         (находка владельца 2026-09-25). Сама рамка - прозрачная. */
      className="absolute p-0"
      data-layout-watermark
      title="Водяной знак: условие бесплатного тарифа. Открыть настройки"
      style={{
        left: `${(box.left / FRAME_WIDTH) * 100}%`,
        top: `${(box.top / FRAME_HEIGHT) * 100}%`,
        width: `${((box.right - box.left) / FRAME_WIDTH) * 100}%`,
        height: `${((box.bottom - box.top) / FRAME_HEIGHT) * 100}%`,
      }}
      onPointerDown={(event) => {
        // Нажатие не доходит до подложки: та снимает выделение части.
        event.stopPropagation();
      }}
      onClick={() => {
        void navigate('/app/settings/watermark');
      }}
    >
      {/* Над рамкой, пока над ней есть место; иначе - внутрь, под верхнюю
          кромку знака (решение владельца 2026-09-25). */}
      <span
        data-layout-watermark-label={above ? 'above' : 'inside'}
        className={`absolute start-0 whitespace-nowrap ${above ? '-top-5' : 'top-0'}`}
      >
        Знак платформы
      </span>
    </Button>
  );
}

/** Охранные зоны TikTok - предупреждение, а не запрет. */
function GuardZones() {
  return (
    <>
      {guardZones().map((zone) => (
        <div
          key={zone.id}
          data-layout-guard={zone.id}
          aria-hidden
          className="border-warning/50 bg-warning/10 pointer-events-none absolute border border-dashed"
          style={{
            left: `${(zone.box.x / FRAME_WIDTH) * 100}%`,
            top: `${(zone.box.y / FRAME_HEIGHT) * 100}%`,
            width: `${(zone.box.width / FRAME_WIDTH) * 100}%`,
            height: `${(zone.box.height / FRAME_HEIGHT) * 100}%`,
          }}
        >
          {/*
            ПОДПИСЬ РАСКРЫВАЕТСЯ ВНУТРЬ КАДРА, А НЕ ОБРЕЗАЕТСЯ ЗОНОЙ.

            Прежняя редакция держала её `max-w-full truncate` внутри самой
            зоны: правая охранная полоса узкая, и подпись превращалась в
            «Здесь кн…» - находка владельца. Ширина зоны не имеет отношения
            к длине её названия, поэтому подпись прижата к правому краю
            зоны и растёт ВЛЕВО, поверх оверлея. Плашка непрозрачная: на
            шахматке прозрачности оранжевый текст терял контраст.
          */}
          <span className="text-warning absolute top-0.5 right-1 rounded bg-black/75 px-1 text-xs whitespace-nowrap">
            {zone.title}
          </span>
        </div>
      ))}
    </>
  );
}

/**
 * Подвижная часть: рамка, подпись над рамкой, угловая ручка.
 *
 * Клавиатура равноправна мыши: элемент выбирается табуляцией, двигается
 * стрелками, Shift ускоряет шаг вдесятеро. Редактор, доступный только мышью,
 * просто не существует для части людей.
 */
function Movable({
  element,
  editor,
  scale,
}: {
  element: HudElementSpec;
  editor: LayoutEditor;
  scale: number;
}) {
  const placement = editor.layout[element.id];
  const box = boxOf(element, placement);
  const selected = editor.selected === element.id;
  const hidden = placement?.hidden === true;
  const labelAbove = captionFits(box.y, scale);

  const drag = useRef<{ x: number; y: number } | null>(null);
  const [hover, setHover] = useState(false);

  const onPointerMove = useCallback(
    (event: React.PointerEvent) => {
      if (drag.current === null) return;

      const host = (event.currentTarget as HTMLElement).parentElement;
      if (host === null) return;

      const rect = host.getBoundingClientRect();
      const dx = ((event.clientX - drag.current.x) / rect.width) * FRAME_WIDTH;
      const dy = ((event.clientY - drag.current.y) / rect.height) * FRAME_HEIGHT;

      if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return;

      drag.current = { x: event.clientX, y: event.clientY };
      editor.move(element, dx, dy, !event.altKey);
    },
    [editor, element],
  );

  return (
    <div
      role="button"
      tabIndex={0}
      data-layout-movable={element.id}
      /* Объявленные игрой «лежит поверх» уезжают в разметку: по ним ворота
         заводской раскладки отличают слоистость от наложения. */
      data-layout-over={(element.over ?? []).join(' ')}
      aria-label={`${editor.label(element)}${hidden ? ', скрыт' : ''}`}
      aria-pressed={selected}
      /* Три состояния рамки. Одинаковая рамка у двенадцати частей не
         отвечает на вопрос «что сейчас под курсором и что выбрано». */
      className={`absolute cursor-grab active:cursor-grabbing ${
        selected
          ? 'border-primary border-2'
          : hover
            ? 'border-foreground/70 border border-solid'
            : 'border-foreground/30 border border-dashed'
      } ${hidden ? 'opacity-40' : ''}`}
      onPointerEnter={() => {
        setHover(true);
      }}
      onPointerLeave={() => {
        setHover(false);
      }}
      onFocus={() => {
        setHover(true);
      }}
      onBlur={() => {
        setHover(false);
      }}
      style={{
        left: `${(box.x / FRAME_WIDTH) * 100}%`,
        top: `${(box.y / FRAME_HEIGHT) * 100}%`,
        width: `${(box.width / FRAME_WIDTH) * 100}%`,
        height: `${(box.height / FRAME_HEIGHT) * 100}%`,
      }}
      onPointerDown={(event) => {
        event.stopPropagation();
        editor.select(element.id);
        drag.current = { x: event.clientX, y: event.clientY };
        (event.target as HTMLElement).setPointerCapture(event.pointerId);
      }}
      onPointerMove={onPointerMove}
      onPointerUp={() => {
        drag.current = null;
      }}
      onPointerCancel={() => {
        drag.current = null;
      }}
      onKeyDown={(event) => {
        const step = event.shiftKey ? FAST_STEP : STEP;
        const by: Record<string, [number, number]> = {
          ArrowLeft: [-step, 0],
          ArrowRight: [step, 0],
          ArrowUp: [0, -step],
          ArrowDown: [0, step],
        };

        const delta = by[event.key];

        if (delta === undefined) return;

        event.preventDefault();
        editor.select(element.id);
        editor.move(element, delta[0], delta[1], false);
      }}
    >
      {/*
        Подпись показывается ТОЛЬКО у наведённой или выбранной части. Двенадцать
        подписей разом закрывали содержимое оверлея - стример переставал видеть
        то, что двигает. Плашка непрозрачная, кегль мельче игрового текста:
        служебный слой не должен спорить с содержимым.
      */}
      {(hover || selected) && (
        <span
          data-layout-label={labelAbove ? 'above' : 'inside'}
          /* Над рамкой, пока над ней есть место; у самой верхней кромки
             оверлея - внутрь, под кромку части. То же правило, что у
             подписи водяного знака. */
          className={`pointer-events-none absolute left-0 rounded px-1 text-xs whitespace-nowrap ${
            labelAbove ? '-top-4' : 'top-0'
          } ${selected ? 'bg-primary text-primary-foreground' : 'text-foreground bg-black/85'}`}
        >
          {editor.label(element)}
        </span>
      )}

      {selected && (
        <span
          role="presentation"
          data-layout-resize={element.id}
          className="bg-primary absolute -right-1 -bottom-1 size-2.5 cursor-nwse-resize rounded-sm"
          onPointerDown={(event) => {
            event.stopPropagation();
            (event.target as HTMLElement).setPointerCapture(event.pointerId);
          }}
          onPointerMove={(event) => {
            if (event.buttons === 0) return;

            const host = (event.currentTarget as HTMLElement).closest(
              '[data-console-layout-editor]',
            );
            if (host === null) return;

            const rect = host.getBoundingClientRect();
            const wanted = ((event.clientX - rect.left) / rect.width) * FRAME_WIDTH - box.x;
            const scale = Math.max(0.4, Math.min(3, wanted / element.width));

            editor.patch(element.id, { scale: Math.round(scale * 20) / 20 });
          }}
        />
      )}
    </div>
  );
}

/** Направляющие снапа: видно, к чему притянулось. */
function Guides({ editor }: { editor: LayoutEditor }) {
  return (
    <>
      {editor.guides.vertical.map((x) => (
        <div
          key={`v${x}`}
          aria-hidden
          className="bg-primary/70 pointer-events-none absolute top-0 bottom-0 w-px"
          style={{ left: `${(x / FRAME_WIDTH) * 100}%` }}
        />
      ))}
      {editor.guides.horizontal.map((y) => (
        <div
          key={`h${y}`}
          aria-hidden
          className="bg-primary/70 pointer-events-none absolute right-0 left-0 h-px"
          style={{ top: `${(y / FRAME_HEIGHT) * 100}%` }}
        />
      ))}
    </>
  );
}

/**
 * Управление режимом - В ПАНЕЛИ ИНСТРУМЕНТОВ КАДРА, столбцом справа от него.
 *
 * Внутри кадра любая панель закрывает то, ради чего режим включён. До
 * единицы 21 полоса стояла строкой над кадром и деградировала по своей
 * ширине, пряча подписи и кнопки по порогам; решение владельца 2026-09-29
 * (2) отдало кадру всю высоту колонки, а органы - в столбец сбоку.
 *
 * СТОЛБЕЦ ИЗ ЗНАЧКОВ, И НИЧЕГО НЕ ПРЯЧЕТСЯ. В строке прятать приходилось,
 * потому что ширина центра тянется мышью; высота колонки - нет, и девять
 * значков по 28 px в неё помещаются всегда. Имя каждого - в `aria-label` и
 * подсказке. Подпись режима и подсказка «чем двигать» - в справке (значок
 * «i»), а не строкой, которую пришлось бы обрезать.
 *
 * «Готово» - НЕ ГЛАВНАЯ. Главная кнопка консоли одна и снимает блокировку
 * эфира (блок 2 единицы 21); выход из режима - первая в столбце и с
 * галочкой, её не спутать.
 */
function Controls({
  data,
  editor,
  onExit,
}: {
  data: LayoutPayload;
  editor: LayoutEditor;
  onExit: () => void;
}) {
  const selected = data.elements.find((element) => element.id === editor.selected) ?? null;
  const hidden = selected !== null && editor.layout[selected.id]?.hidden === true;
  const pick = 'Выберите часть оверлея';

  return (
    <div
      data-layout-controls
      data-control-row="frame-layout"
      className="border-foreground/15 bg-muted flex flex-col items-center gap-1 rounded-lg border p-1"
    >
      {/*
        ЕДИНСТВЕННЫЙ ВЫХОД ИЗ РЕЖИМА. Вторая такая кнопка стояла справа от
        оверлея - одно действие в двух местах, и стример выбирал между ними
        вместо того, чтобы просто закончить.
      */}
      <Button
        variant="secondary"
        size="s"
        iconOnly
        data-layout-exit
        aria-label="Готово"
        title="Готово — выйти из режима раскладки"
        onClick={onExit}
      >
        <Icon name="check" size="sm" />
      </Button>

      {/* У недоступной кнопки названа ПРИЧИНА - в подсказке: серый значок
          сам по себе читается как плохая вёрстка, а не как «пока нельзя». */}
      <Button
        variant="ghost"
        size="s"
        iconOnly
        data-layout-undo
        aria-label="Отменить"
        title={editor.canUndo ? 'Отменить' : 'Отменить — нечего отменять'}
        disabled={!editor.canUndo}
        onClick={editor.undo}
      >
        <Icon name="arrowReset" size="sm" />
      </Button>

      <Button
        variant="ghost"
        size="s"
        iconOnly
        data-layout-restore-one
        aria-label="Вернуть на место"
        title={selected === null ? `Вернуть на место — ${pick.toLowerCase()}` : 'Вернуть на место'}
        disabled={selected === null}
        onClick={() => {
          if (selected !== null) editor.restore(selected.id);
        }}
      >
        <Icon name="target" size="sm" />
      </Button>

      {/* Скрыть можно ЛЮБОЙ элемент - правило нашего редактора, которого у
          эталона нет. При переезде на оверлей оно не теряется. */}
      <Button
        variant="ghost"
        size="s"
        iconOnly
        data-layout-hide
        aria-label={hidden ? 'Показать' : 'Скрыть'}
        aria-pressed={hidden}
        title={
          selected === null ? `Скрыть — ${pick.toLowerCase()}` : hidden ? 'Показать' : 'Скрыть'
        }
        disabled={selected === null}
        onClick={() => {
          if (selected === null) return;

          editor.patch(selected.id, { hidden: hidden ? undefined : true });
        }}
      >
        <Icon name={hidden ? 'eye' : 'eyeOff'} size="sm" />
      </Button>

      {/*
        ПОРЯДОК СЛОЁВ ПРАВИТСЯ (ARCHITECTURE 5.2, строка 12). У эталона слои
        жёстко заданы разметкой: игровое поле всегда под всем остальным, и
        поставить таблицу лидеров под каллаут нельзя ничем. Здесь слой -
        такое же свойство положения, как смещение.
      */}
      <Button
        variant="ghost"
        size="s"
        iconOnly
        data-layout-up
        aria-label="Поднять над соседями"
        title={selected === null ? `Поднять — ${pick.toLowerCase()}` : 'Поднять над соседями'}
        disabled={selected === null}
        onClick={() => {
          if (selected !== null) {
            editor.patch(selected.id, { z: (editor.layout[selected.id]?.z ?? 1) + 1 });
          }
        }}
      >
        <Icon name="chevronUp" size="sm" />
      </Button>

      <Button
        variant="ghost"
        size="s"
        iconOnly
        data-layout-down
        aria-label="Опустить под соседей"
        title={selected === null ? `Опустить — ${pick.toLowerCase()}` : 'Опустить под соседей'}
        disabled={selected === null}
        onClick={() => {
          if (selected !== null) {
            editor.patch(selected.id, {
              z: Math.max(0, (editor.layout[selected.id]?.z ?? 1) - 1),
            });
          }
        }}
      >
        <Icon name="chevronDown" size="sm" />
      </Button>

      {data.sources.length > 0 && (
        <span data-layout-copy className="contents">
          <Popover
            label="Перенести раскладку из другой игры"
            kind="menu"
            triggerVariant="ghost"
            triggerIconOnly
            trigger={<Icon name="copy" size="sm" />}
            className="w-60 p-1"
          >
            {data.sources.map((source) => (
              <Button
                key={source.gameId}
                variant="ghost"
                className="w-full justify-start"
                data-layout-copy-from={source.gameId}
                data-popover-close
                onClick={() => {
                  editor.copyFrom(source.gameId);
                }}
              >
                {source.gameId} ({source.elements})
              </Button>
            ))}
          </Popover>
        </span>
      )}

      <Destructive
        id="layout-reset"
        data={{ 'data-layout-reset': '' }}
        ariaLabel="Сбросить раскладку"
        icon={<Icon name="delete" size="sm" />}
        disabled={editor.resetPending}
        title="Сбросить раскладку?"
        confirm="Сбросить всё"
        onConfirm={editor.resetAll}
      >
        Все части оверлея этой игры вернутся на заводские места и размеры, скрытые снова появятся.
      </Destructive>

      {/*
        Справка режима. У эталона она висит плашкой рядом с ручкой и
        объясняет три вещи: чем двигать, чем менять размер, чем закончить.
        Без неё стример видит дюжину одинаковых пунктиров и шесть кнопок.
      */}
      <Popover
        label="Как двигать части оверлея"
        triggerVariant="ghost"
        triggerIconOnly
        trigger={<Icon name="info" size="sm" />}
        className="w-64 p-3"
      >
        <p className="text-sm font-semibold" data-layout-count>
          {parts(data.elements.length)}
        </p>
        <p className="text-foreground/80 mt-1 text-sm">
          Тяните часть — двигать, угол — размер, Alt — без привязки. Закончить — галочка вверху.
        </p>
      </Popover>
    </div>
  );
}

/** «12 частей», а не «12 часть»: число и слово согласуются. */
function parts(count: number): string {
  const tail = count % 100;
  const last = count % 10;

  if (tail >= 11 && tail <= 14) return `Подвижных частей: ${count}`;
  if (last === 1) return `Подвижная часть: ${count}`;
  if (last >= 2 && last <= 4) return `Подвижные части: ${count}`;

  return `Подвижных частей: ${count}`;
}

/** Шаг снапа наружу - его печатает подсказка режима. */
export const LAYOUT_SNAP = SNAP;
